/**
 * BoardLinkPersonModal — quick-action modal for the Portfolio board's
 * Manager and Ambassador columns. Lists active users (filtered to
 * is_ambassador=true when kind='ambassador'), with a search input and
 * a click-to-link list. Writes hosp_properties.assigned_manager_id or
 * .ambassador_id directly.
 *
 * For ambassadors, an explicit "No ambassador" option sits at the top:
 * picking it nulls ambassador_id and signals the operator has
 * consciously decided to forfeit commission, rather than leaving it
 * silently empty.
 */

import { useEffect, useMemo, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useToast } from '../../../../../components/Toast';
import { useAuth } from '../../../../../contexts/AuthContext';
import LoadingSpinner from '../../../../../components/LoadingSpinner';

interface Props {
  supabase: SupabaseClient;
  propertyId: string;
  propertyName: string;
  kind: 'manager' | 'ambassador';
  currentValue: string | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

interface UserOption {
  id: string;
  name: string;
  email: string | null;
}

export default function BoardLinkPersonModal({
  supabase, propertyId, propertyName, kind, currentValue, onClose, onSaved,
}: Props) {
  const { showToast } = useToast();
  const { hasPermission, isSuperAdmin } = useAuth();
  // Ambassador attribution is a payout decision, locked to payouts:manage admins
  // server-side. Block the write here so a non-admin gets a clear message rather
  // than a success toast over a change the database silently reverts.
  const canSetAmbassador = isSuperAdmin || hasPermission('payouts', 'manage');
  const [options, setOptions] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      let query = supabase
        .from('users')
        .select('id, first_name, last_name, email')
        .eq('status', 'active');
      if (kind === 'ambassador') query = query.eq('is_ambassador', true);
      const { data, error } = await query.order('first_name');
      if (cancelled) return;
      if (error) {
        showToast(`Could not load ${kind}s: ${error.message}`, 'error');
        setOptions([]);
      } else {
        setOptions((data ?? []).map((u: Record<string, unknown>) => ({
          id: u.id as string,
          name: `${(u.first_name as string) || ''} ${(u.last_name as string) || ''}`.trim() || (u.email as string) || '—',
          email: (u.email as string) || null,
        })));
      }
      setLoading(false);
    }
    void load();
    return () => { cancelled = true; };
  }, [supabase, kind, showToast]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) =>
      o.name.toLowerCase().includes(term) || (o.email ?? '').toLowerCase().includes(term),
    );
  }, [options, search]);

  async function handlePick(value: string | null) {
    if (saving) return;
    if (kind === 'ambassador' && !canSetAmbassador) {
      showToast('Ambassador assignment is an admin function (payouts).', 'error');
      return;
    }
    setSaving(true);
    const column = kind === 'ambassador' ? 'ambassador_id' : 'assigned_manager_id';
    const { error } = await supabase
      .from('hosp_properties')
      .update({ [column]: value })
      .eq('id', propertyId);
    setSaving(false);
    if (error) {
      showToast(`Could not link ${kind}: ${error.message}`, 'error');
      return;
    }
    const label = value === null ? `No ${kind}` : options.find((o) => o.id === value)?.name ?? 'selection';
    showToast(`${label} linked to ${propertyName}.`, 'success');
    await onSaved();
    onClose();
  }

  const title = kind === 'ambassador' ? 'Link ambassador' : 'Link manager';

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1100 }}>
      <div className="modal modal-md" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{title} to {propertyName}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <input
            type="search"
            className="form-input"
            placeholder={`Search ${kind}s by name or email…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          {loading ? (
            <LoadingSpinner />
          ) : (
            <ul className="picker-list">
              {kind === 'ambassador' && (
                <li>
                  <button
                    type="button"
                    className={`picker-row ${currentValue === null ? 'picker-row--current' : ''}`}
                    onClick={() => handlePick(null)}
                    disabled={saving}
                  >
                    <span className="picker-row-name">No ambassador</span>
                    <span className="picker-row-meta">Property carries no commission. Make this an explicit decision.</span>
                  </button>
                </li>
              )}
              {filtered.length === 0 ? (
                <li className="picker-list-empty">No matches.</li>
              ) : filtered.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    className={`picker-row ${currentValue === o.id ? 'picker-row--current' : ''}`}
                    onClick={() => handlePick(o.id)}
                    disabled={saving}
                  >
                    <span className="picker-row-name">{o.name}</span>
                    {o.email && <span className="picker-row-meta">{o.email}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
